import {
  clearPendingSponsorPayment,
  readPendingSponsorPayment,
} from "@/lib/payments/sponsorPaymentRecovery.js";
import { syncSponsorPaymentSession } from "@/lib/payments/api.ts";
import { listRecoverableSponsorPaymentLinks } from "@/services/billingLedgerService.js";

function readPendingRecoverySources(role = "patient") {
  if (role === "doctor") {
    return ["appointment_checkout", "preferences_transactions"];
  }

  return ["appointment_checkout", "preferences_transactions"];
}

function isIgnorablePendingSyncError(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    message.includes("not completed") ||
    message.includes("timed out") ||
    message.includes("timeout")
  );
}

export function dispatchSponsorRecoveryEvents(syncResult = {}) {
  window.dispatchEvent(new Event("afrimed:transactions:changed"));

  if (String(syncResult?.appointment_id || "").trim()) {
    window.dispatchEvent(new Event("afrimed:appointments:changed"));
    window.dispatchEvent(new Event("afrimed:notifications:changed"));
  }
}

export function readPendingSponsorRecoveryCandidate(userId = "", role = "patient") {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) return { source: "", pending: null };

  for (const source of readPendingRecoverySources(role)) {
    const pending = readPendingSponsorPayment(cleanUserId, source);
    if (pending?.paymentLinkId) {
      return { source, pending };
    }
  }

  return { source: "", pending: null };
}

export async function recoverSponsorPayments({
  role = "patient",
  userId = "",
  sessionId = "",
} = {}) {
  const cleanUserId = String(userId || "").trim();
  const cleanSessionId = String(sessionId || "").trim();
  const pendingRecoveryCandidate = readPendingSponsorRecoveryCandidate(cleanUserId, role);
  const pendingRecovery = pendingRecoveryCandidate.pending;
  const pendingRecoverySource = pendingRecoveryCandidate.source;
  let recoveredCount = 0;
  let lastError = null;

  if (!cleanUserId) {
    return {
      recoveredCount,
      lastError,
      usedSessionId: false,
      pendingRecoverySource,
    };
  }

  if (cleanSessionId) {
    try {
      const syncResult = await syncSponsorPaymentSession({ session_id: cleanSessionId });
      dispatchSponsorRecoveryEvents(syncResult);
      recoveredCount += 1;
    } catch (error) {
      lastError = error;
    }
  } else if (pendingRecovery?.paymentLinkId) {
    try {
      const syncResult = await syncSponsorPaymentSession({
        payment_link_id: String(pendingRecovery.paymentLinkId || "").trim(),
        recovery_hint:
          pendingRecovery?.recoveryHint && typeof pendingRecovery.recoveryHint === "object"
            ? pendingRecovery.recoveryHint
            : undefined,
      });
      clearPendingSponsorPayment(cleanUserId, pendingRecoverySource);
      dispatchSponsorRecoveryEvents(syncResult);
      recoveredCount += 1;
    } catch (error) {
      if (!isIgnorablePendingSyncError(error)) {
        lastError = error;
      }
    }
  }

  if (role === "patient" || role === "doctor") {
    try {
      const pendingLinks = await listRecoverableSponsorPaymentLinks(role, cleanUserId);
      for (const item of pendingLinks) {
        try {
          const syncResult = await syncSponsorPaymentSession({
            payment_link_id: String(item?.paymentLinkId || "").trim(),
          });
          dispatchSponsorRecoveryEvents(syncResult);
          recoveredCount += 1;
        } catch (error) {
          if (isIgnorablePendingSyncError(error)) {
            continue;
          }

          lastError = lastError || error;
          break;
        }
      }
    } catch (error) {
      lastError = lastError || error;
    }
  }

  return {
    recoveredCount,
    lastError,
    usedSessionId: !!cleanSessionId,
    pendingRecoverySource,
  };
}
